import * as API from '@/API';
import type { MultiSelectOption } from '@/Components/MultiSelect/MultiSelect';
import type NewWord from '@/Models/NewWords/NewWord';
import NewWordStatus from '@/Models/NewWords/NewWordStatus';
import NewWordsFilter from '@/Models/NewWords/NewWordsFilter';
import type FilingCabinetStore from '@/Stores/FilingCabinetStore';
import NewWordsDetailStore from '@/Stores/NewWords/NewWordsDetailStore';
import type { ColumnSort, PaginationState, RowSelectionState, SortingState, Table, VisibilityState } from '@tanstack/react-table';
import { t } from 'i18next';
import { action, makeObservable, observable } from 'mobx';

interface StateData {
    newWords: NewWord[];
    wordCount: number;
    pagination: PaginationState;
    sorting: ColumnSort[];
    rowSelection: RowSelectionState;
    globalFilter: string;
    selectedNewWordsState: number;
    languages: MultiSelectOption[];
    chosenLanguages: MultiSelectOption[];
    users: MultiSelectOption[];
    chosenUsers: MultiSelectOption[];
    selectedWords: NewWord[];
    currentUserIsAdmin: boolean;
    table: Table<NewWord>;
    columnVisibility: VisibilityState;
}

export default class NewWordsStore {
    state: StateData;

    detailStore: NewWordsDetailStore = new NewWordsDetailStore();
    loading = true;

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(fcStore: FilingCabinetStore<StateData>) {
        this.fcStore = fcStore;

        this.state = {
            globalFilter: '',
            selectedNewWordsState: NewWordStatus.New,
            newWords: [],
            wordCount: 0,
            languages: [],
            chosenLanguages: [],
            users: [],
            chosenUsers: [],
            pagination: { pageIndex: 0, pageSize: 25 },
            sorting: [],
            rowSelection: {},
            selectedWords: [],
            currentUserIsAdmin: false,
            table: {} as Table<NewWord>,
            columnVisibility: {
                ID: true,
                Word: true,
                Language: true,
                LastUsed: true,
                LastModification: true,
                Owner: false,
            },
        };

        makeObservable(this, {
            state: observable,
            loading: observable,
            setGlobalFilter: action,
            setSelectedNewWordsStatusAndReloadTable: action,
            setNewWords: action,
            setChosenUsers: action,
            setChosenLanguages: action,
            setState: action,
            setPagination: action,
            setRowSelection: action,
            setSorting: action,
            reload: action,
            setLoading: action,
        });

        this.fcStore.initializeState(this.initOld, this.initNew);
        this.fcStore.updateTitle(t('NewWords'));
    }

    private readonly initOld = (state: StateData): void => {
        this.setState({
            globalFilter: state.globalFilter,
            selectedNewWordsState: state.selectedNewWordsState,
            languages: state.languages,
            users: state.users,
            newWords: state.newWords,
            wordCount: state.wordCount,
            pagination: state.pagination,
            sorting: state.sorting,
            chosenLanguages: state.chosenLanguages,
            chosenUsers: state.chosenUsers,
            rowSelection: state.rowSelection,
            currentUserIsAdmin: state.currentUserIsAdmin,
            table: state.table,
            columnVisibility: state.columnVisibility,
        });
        this.setLoading(false);
    };

    private readonly initNew = (): void => {
        this.setLoading(true);
        API.setup.newWords.newWordsOverview()
            .then((hydrateData) => {
                this.setState({
                    globalFilter: '',
                    selectedNewWordsState: 1,
                    languages: hydrateData.Languages,
                    chosenLanguages: hydrateData.Languages,
                    users: hydrateData.Users,
                    chosenUsers: hydrateData.Users,
                    pagination: { pageIndex: 0, pageSize: 25 },
                    sorting: [],
                    rowSelection: {},
                    currentUserIsAdmin: hydrateData.CurrentUserIsAdmin,
                    columnVisibility: {
                        ID: true,
                        Word: true,
                        Language: true,
                        LastUsed: true,
                        LastModification: true,
                        Owner: hydrateData.CurrentUserIsAdmin,
                    },
                });
                this.reload();
            })
            .catch((err) => {
                console.error('Could not load NewWords overview', err);
                this.setLoading(false);
            });
    };

    reload = (): void => {
        this.setNewWords([]);
        this.setLoading(true);
        API.newWords.getNewWords(
            {
                TextFilter: this.state.globalFilter,
                LanguageFilter: this.state.chosenLanguages,
                UserFilter: this.state.chosenUsers,
            } satisfies NewWordsFilter,
            this.state.selectedNewWordsState,
            this.state.pagination.pageIndex, this.state.pagination.pageSize)
            .then((res) => {
                this.setNewWords(res.Words);
                this.setWordCount(res.TotalAmount);
                this.setLoading(false);
            })
            .catch((err) => {
                console.error('Could not reload NewWords overview', err);
                this.setLoading(false);
            });
    };

    setState = (newState: any): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    setGlobalFilter = (filter: string): void => {
        this.state.globalFilter = filter;
        this.fcStore.updateState(this.state);
    };

    setWordCount = (val: number): void => {
        this.state.wordCount = val;
        this.fcStore.updateState(this.state);
    };

    setSelectedNewWordsStatusAndReloadTable = (val: number): void => {
        this.state.selectedNewWordsState = val;
        this.fcStore.updateState(this.state);

        this.state.table.getState().pagination.pageIndex = 0;
        this.reload();
    };

    setNewWords = (val: NewWord[]): void => {
        this.state.newWords = val;
        this.fcStore.updateState(this.state);
    };

    setPagination = (val: PaginationState): void => {
        this.state.pagination = val;
        this.fcStore.updateState(this.state);
        this.reload();
    };

    setSorting = (val: SortingState): void => {
        this.state.sorting = val;
        this.fcStore.updateState(this.state);
    };

    setRowSelection = (val: RowSelectionState): void => {
        this.state.rowSelection = val;
        this.fcStore.updateState(this.state);
    };

    setChosenLanguages = (val: any): void => {
        this.state.chosenLanguages = val;
        this.fcStore.updateState(this.state);
    };

    setChosenUsers = (val: any): void => {
        this.state.chosenUsers = val;
        this.fcStore.updateState(this.state);
    };

    setLoading = (val: boolean): void => {
        this.loading = val;
    };

    setSelectedWordAndOpenDialog = (word: NewWord | undefined): void => {
        this.detailStore.setSelectedWord(word);
        this.detailStore.setDialogIsOpen(true);
    };

    setSelectedWords = (selectedWords: NewWord[]): void => {
        this.state.selectedWords = selectedWords;
    };

    approveAllSelectedWords = async (): Promise<void> => {
        const approvePromises = this.state.selectedWords.map((selectedWord) => API.newWords.approveNewWord(selectedWord.ID));
        await Promise.all(approvePromises);

        this.clearRowSelectionAndReload();
    };

    ignoreAllSelectedWords = async (): Promise<void> => {
        const ignorePromises = this.state.selectedWords.map((selectedWord) => API.newWords.ignoreNewWord(selectedWord.ID));
        await Promise.all(ignorePromises);

        this.clearRowSelectionAndReload();
    };

    clearRowSelectionAndReload = (): void => {
        this.setRowSelection({});
        this.state.selectedWords = [];
        this.reload();
    };

    addOrEditWordAndCloseDialog = async (changedWordStatus: NewWordStatus): Promise<void> => {
        const detailstoreState = this.detailStore.state;
        const newWord = {
            ID: detailstoreState.wordID,
            Word: detailstoreState.word,
            Language: detailstoreState.language,
            Status: changedWordStatus ?? this.state.selectedNewWordsState,
        } as NewWord;

        const result = await API.newWords.addOrEditNewWord(newWord);
        if (result.AllOk) {
            this.reload();

            this.detailStore.setDialogIsOpen(false);
        }
        else {
            this.detailStore.state.error = result.ErrorMessage;
        }
    };
}
